import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { isCandidateScopedPath, type ApiError } from '@/api/http';
import { Button } from '@/components/ui/Button';

import { ErrorState } from './ErrorState';
import { ForbiddenState } from './ForbiddenState';
import { OfflineState } from './OfflineState';

interface Props {
  error: ApiError;
  /** The screen's own sentence for an ordinary failure; the other states carry their own copy. */
  message?: string;
  /**
   * The Forbidden state's sentence, for a screen whose refusal is **on** the candidate surface but is
   * not about a candidate it can name.
   *
   * ⚠️ It exists for one screen (§6.6/§6.7, TASK-080) and the reason is the payload rather than a
   * preference: a 404 from `/api/v1/interviews/{id}/` is a reach answer like any other on this
   * surface, but a blinded interview carries **no `person_id`** — no candidate and no vacancy on it at
   * all (ADR 0006; the one name it does carry is the *interviewer's*) — so *"You do not have access to
   * this candidate."* names a person this screen
   * could not identify even when it is answered. The interview screen says *conversation* instead,
   * which is what the reader named. It discloses no more: the same sentence answers an id that never
   * existed, which is the property TASK-029's 404 depends on.
   *
   * Left unset, the mapping below is unchanged — the §8 candidate sentence on the candidate surface,
   * *"this screen"* everywhere else — so no other caller is affected by this prop existing.
   */
  forbiddenMessage?: string;
  onRetry?: () => void;
  /** The way back out of the Forbidden state — §8 states are not dead ends. */
  action?: ReactNode;
}

/**
 * One place that turns a failed request into one of the §8 states, so no screen decides it twice.
 *
 * ⚠️ **A 404 from a candidate route is the Forbidden state, not an error** (backend TASK-029,
 * settled 2026-07-24, `DESIGN.md` §8). The API answers 404 — never 403 — when an `Interviewer`
 * reaches past their assignment, because a 403 would confirm that a candidate with that id exists
 * in the tenant. That decision only holds up if the screen renders *"You do not have access to this
 * candidate."* for it rather than "something went wrong", and the rule lives here rather than in
 * the candidate screens because those do not exist yet: it is in place before the first one asks.
 *
 * The mapping, in the order it is decided:
 *
 * | Answer | State |
 * |---|---|
 * | no response at all | Offline (§8.5) — with a Retry, never a lost page |
 * | 403, or 404 from a candidate route | Forbidden (§8.4) |
 * | 429 | Error (§8.3), saying it is a throttle rather than a fault |
 * | 502, 503, 504 | Error (§8.3), saying the server is unreachable and that nobody was signed out |
 * | anything else | Error (§8.3), in the screen's own words |
 *
 * A 401 has no row of its own: it ends the session and returns to sign-in (TASK-056,
 * `src/app/queryClient.ts`), so whatever this renders for one is gone before it is read.
 *
 * ⚠️ **The unreachable row overrides the screen's own sentence, exactly as the throttle row does**
 * (BUG-103). *"The dashboard could not be loaded"* is true of a 502 and useless for it: the reader's
 * next step is to wait rather than to look at the dashboard, and the sentence has to say that the
 * failure is not theirs — a backend outage presenting as anything about the reader's account is the
 * whole of the bug this row was added for.
 */
export function ApiErrorState({ error, message, forbiddenMessage, onRetry, action }: Props) {
  const { t } = useTranslation(['components', 'common']);

  if (error.isOffline) {
    return (
      <div className="flex flex-col items-start gap-3">
        <OfflineState />
        {onRetry && (
          <Button variant="secondary" onClick={onRetry}>
            {t('common:retry')}
          </Button>
        )}
      </div>
    );
  }

  if (error.status === 403 || error.isOutsideReach) {
    // The §8 sentence is about a candidate, because that is the case the design fixes. A refusal
    // somewhere else — a tenant-less superuser on the dashboard, say — gets the sentence that is
    // true for it rather than one about a candidate nobody named. A screen on the surface with a
    // truer sentence than either passes it (`forbiddenMessage`; §6.6/§6.7 is the one).
    return (
      <ForbiddenState
        action={action}
        message={
          forbiddenMessage ??
          (isCandidateScopedPath(error.path) ? undefined : t('components:state.forbiddenScreen'))
        }
      />
    );
  }

  return (
    <ErrorState
      message={
        error.isThrottled
          ? t('components:state.throttled')
          : error.isUnreachable
            ? t('components:state.unreachable')
            : message
      }
      onRetry={onRetry}
    />
  );
}
